import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import {
  useNavigate,
} from "react-router-dom";
import {

  getDashboardEvaluations,
  getDashboardIntegration,
} from "../services/dashboardApi";
import { SAMPLE_EVALUATIONS, shouldUseSampleData } from "../data/sampleDashboardData";

const verdictClasses = {
  ALLOW: "bg-success-focus text-success-main",
  REVIEW: "bg-warning-focus text-warning-main",
  BLOCK: "bg-danger-focus text-danger-main",
};

const riskClass = (score) => {
  if (score >= 80) {
    return "text-danger-main";
  }

  if (score >= 50) {
    return "text-warning-main";
  }

  return "text-success-main";
};

const score100 = (value) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return 0;
  }

  if (numeric <= 1) {
    return Math.round(numeric * 100);
  }

  return Math.round(numeric);
};

const humanize = (value) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "Unknown";
  }

  return String(value)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
};

const formatDateTime = (value) => {
  if (!value) {
    return {
      date: "Unavailable",
      time: "",
    };
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return {
      date: "Unavailable",
      time: "",
    };
  }

  return {
    date: date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),

    time: date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const normalizeReasons = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (typeof item === "string") {
        return item;
      }

      return (
        item?.code ||
        item?.name ||
        item?.reason ||
        null
      );
    })
    .filter(Boolean);
};

const normalizeEvaluation = (item) => {
  const id =
    item?.id ||
    item?.evaluationId ||
    item?.evaluation_id ||
    "";

  const createdAt =
    item?.createdAt ||
    item?.created_at ||
    null;

  const dateTime =
    formatDateTime(createdAt);

  const verdict = String(
    item?.verdict ||
      item?.finalVerdict ||
      item?.final_verdict ||
      "REVIEW"
  ).toUpperCase();

  const risk = score100(
    item?.score ??
      item?.finalScore ??
      item?.final_score ??
      item?.riskScore ??
      item?.risk_score
  );

  const email =
    item?.signupEmail ||
    item?.signup_email ||
    item?.email ||
    "Unavailable";

  const reasons = normalizeReasons(
    item?.reasonCodes ??
      item?.reason_codes ??
      item?.reasons ??
      []
  );

  return {
    id,
    email,
    userId: id
      ? `${String(id).slice(0, 8)}…`
      : "—",
    risk,
    verdict,
    reasons,
    createdAt,
    date: dateTime.date,
    time: dateTime.time,
  };
};

const EventsLayer = () => {
  const navigate = useNavigate();

  const [events, setEvents] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState("");

  const [reloadKey, setReloadKey] =
    useState(0);

  const [search, setSearch] =
    useState("");

  const [verdictFilter, setVerdictFilter] =
    useState("ALL");

  const [riskFilter, setRiskFilter] =
    useState("ALL");

  const [dateFilter, setDateFilter] =
    useState("ALL");

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setLoadError("");

      try {
        const integrationResponse = await getDashboardIntegration().catch(() => null);

        const integration = integrationResponse?.integration || null;
        const useSampleData = shouldUseSampleData(integration);

        const response = useSampleData
          ? { evaluations: SAMPLE_EVALUATIONS }
          : await getDashboardEvaluations({
              limit: 100,
              offset: 0,
            });

        if (!active) {
          return;
        }

        const rows =
          response?.evaluations ||
          response?.items ||
          response?.data ||
          [];

        const normalized =
          Array.isArray(rows)
            ? rows.map(
                normalizeEvaluation
              )
            : [];

        setEvents(normalized);
      } catch (error) {
        if (!active) {
          return;
        }

        if (error?.status === 401) {
          navigate("/sign-in", {
            replace: true,
          });
          return;
        }

        setLoadError(
          error?.message ||
            "Unable to load events."
        );

        setEvents([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [reloadKey, navigate]);

  const filteredEvents = useMemo(() => {
    const query =
      search.trim().toLowerCase();

    return events.filter((event) => {
      if (query) {
        const searchable = [
          event.email,
          event.id,
          event.userId,
          event.verdict,
          ...event.reasons,
        ]
          .join(" ")
          .toLowerCase();

        if (!searchable.includes(query)) {
          return false;
        }
      }

      if (
        verdictFilter !== "ALL" &&
        event.verdict !== verdictFilter
      ) {
        return false;
      }

      if (riskFilter === "LOW") {
        if (event.risk >= 50) {
          return false;
        }
      }

      if (riskFilter === "MEDIUM") {
        if (
          event.risk < 50 ||
          event.risk >= 80
        ) {
          return false;
        }
      }

      if (riskFilter === "HIGH") {
        if (event.risk < 80) {
          return false;
        }
      }

      if (dateFilter !== "ALL") {
        if (!event.createdAt) {
          return false;
        }

        const created =
          new Date(event.createdAt);

        if (
          Number.isNaN(
            created.getTime()
          )
        ) {
          return false;
        }

        const now = new Date();

        if (dateFilter === "24H") {
          const cutoff =
            now.getTime() -
            24 * 60 * 60 * 1000;

          if (
            created.getTime() <
            cutoff
          ) {
            return false;
          }
        }

        if (dateFilter === "7D") {
          const cutoff =
            now.getTime() -
            7 * 24 * 60 * 60 * 1000;

          if (
            created.getTime() <
            cutoff
          ) {
            return false;
          }
        }

        if (dateFilter === "30D") {
          const cutoff =
            now.getTime() -
            30 *
              24 *
              60 *
              60 *
              1000;

          if (
            created.getTime() <
            cutoff
          ) {
            return false;
          }
        }
      }

      return true;
    });
  }, [
    events,
    search,
    verdictFilter,
    riskFilter,
    dateFilter,
  ]);

  const resetFilters = () => {
    setSearch("");
    setVerdictFilter("ALL");
    setRiskFilter("ALL");
    setDateFilter("ALL");
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div>
          <h4 className="mb-4">
            Events
          </h4>

          <p className="text-secondary-light mb-0">
            Real signup evaluations processed by Simplif.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <span className="bg-success-focus text-success-main px-12 py-6 rounded-pill fw-semibold text-sm">
            <Icon
              icon="solar:check-circle-outline"
              className="me-1"
            />
            Live
          </span>

          <button
            type="button"
            className="btn btn-outline-primary d-flex align-items-center gap-2"
            disabled={loading}
            onClick={() =>
              setReloadKey(
                (current) =>
                  current + 1
              )
            }
          >
            <Icon
              icon="solar:refresh-outline"
            />
            Refresh
          </button>
        </div>
      </div>

      <div className="card mb-24">
        <div className="card-body p-24">
          <div className="row gy-3">
            <div className="col-xl-4 col-lg-6">
              <label className="form-label fw-semibold">
                Search
              </label>

              <div className="position-relative">
                <Icon
                  icon="solar:magnifer-outline"
                  className="position-absolute top-50 translate-middle-y ms-16 text-secondary-light"
                  width="20"
                />

                <input
                  type="text"
                  className="form-control ps-44"
                  placeholder="Search email, ID or reason..."
                  value={search}
                  onChange={(event) =>
                    setSearch(
                      event.target.value
                    )
                  }
                />
              </div>
            </div>

            <div className="col-xl-2 col-lg-6">
              <label className="form-label fw-semibold">
                Verdict
              </label>

              <select
                className="form-select"
                value={verdictFilter}
                onChange={(event) =>
                  setVerdictFilter(
                    event.target.value
                  )
                }
              >
                <option value="ALL">
                  All verdicts
                </option>

                <option value="ALLOW">
                  Allow
                </option>

                <option value="REVIEW">
                  Review
                </option>

                <option value="BLOCK">
                  Block
                </option>
              </select>
            </div>

            <div className="col-xl-2 col-lg-6">
              <label className="form-label fw-semibold">
                Risk
              </label>

              <select
                className="form-select"
                value={riskFilter}
                onChange={(event) =>
                  setRiskFilter(
                    event.target.value
                  )
                }
              >
                <option value="ALL">
                  All risk
                </option>

                <option value="LOW">
                  Low
                </option>

                <option value="MEDIUM">
                  Medium
                </option>

                <option value="HIGH">
                  High
                </option>
              </select>
            </div>

            <div className="col-xl-2 col-lg-6">
              <label className="form-label fw-semibold">
                Date
              </label>

              <select
                className="form-select"
                value={dateFilter}
                onChange={(event) =>
                  setDateFilter(
                    event.target.value
                  )
                }
              >
                <option value="ALL">
                  All time
                </option>

                <option value="24H">
                  Last 24 hours
                </option>

                <option value="7D">
                  Last 7 days
                </option>

                <option value="30D">
                  Last 30 days
                </option>
              </select>
            </div>

            <div className="col-xl-2 col-lg-6 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-outline-secondary w-100"
                onClick={
                  resetFilters
                }
              >
                Reset filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {loadError && (
        <div
          className="alert alert-danger mb-24"
          role="alert"
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <span>
              {loadError}
            </span>

            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() =>
                setReloadKey(
                  (current) =>
                    current + 1
                )
              }
            >
              Try again
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header border-bottom bg-base py-16 px-24">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <h6 className="mb-0">
              All events
            </h6>

            {!loading &&
              !loadError && (
                <span className="text-secondary-light text-sm">
                  {
                    filteredEvents.length
                  }{" "}
                  of {events.length}
                </span>
              )}
          </div>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="p-40 text-center">
              <div
                className="spinner-border text-primary-600"
                role="status"
                aria-label="Loading events"
              />

              <p className="text-secondary-light mt-16 mb-0">
                Loading real signup events...
              </p>
            </div>
          ) : loadError ? (
            <div className="p-40 text-center text-secondary-light">
              Events could not be loaded.
            </div>
          ) : filteredEvents.length ===
            0 ? (
            <div className="p-40 text-center">
              <Icon
                icon="solar:inbox-line-outline"
                width="42"
                className="text-secondary-light mb-12"
              />

              <h6 className="mb-8">
                No events found
              </h6>

              <p className="text-secondary-light mb-0">
                No signup evaluations match the current filters.
              </p>
            </div>
          ) : (
            <div
              className="table-responsive"
              style={{
                overflowX: "auto",
              }}
            >
              <table className="table bordered-table mb-0">
                <thead>
                  <tr>
                    <th>
                      Email
                    </th>

                    <th>
                      Evaluation ID
                    </th>

                    <th>
                      Risk Score
                    </th>

                    <th>
                      Verdict
                    </th>

                    <th>
                      Reasons
                    </th>

                    <th>
                      Date & Time
                    </th>

                    <th
                      className="text-center"
                      style={{
                        width: "90px",
                      }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredEvents.map(
                    (event) => (
                      <tr
                        key={
                          event.id
                        }
                      >
                        <td>
                          <div
                            className="fw-semibold"
                            style={{
                              maxWidth:
                                "260px",
                              wordBreak:
                                "break-word",
                            }}
                          >
                            {
                              event.email
                            }
                          </div>
                        </td>

                        <td>
                          <span
                            className="font-monospace text-sm"
                            title={
                              event.id
                            }
                          >
                            {
                              event.userId
                            }
                          </span>
                        </td>

                        <td>
                          <span
                            className={`fw-bold ${riskClass(
                              event.risk
                            )}`}
                          >
                            {
                              event.risk
                            }
                            /100
                          </span>
                        </td>

                        <td>
                          <span
                            className={`px-12 py-6 rounded-pill fw-semibold text-sm ${
                              verdictClasses[
                                event
                                  .verdict
                              ] ||
                              "bg-neutral-200 text-neutral-700"
                            }`}
                          >
                            {
                              event.verdict
                            }
                          </span>
                        </td>

                        <td>
                          {event.reasons
                            .length >
                          0 ? (
                            <div
                              className="d-flex flex-wrap gap-1"
                              style={{
                                maxWidth:
                                  "320px",
                              }}
                            >
                              {event.reasons
                                .slice(
                                  0,
                                  3
                                )
                                .map(
                                  (
                                    reason,
                                    index
                                  ) => (
                                    <span
                                      key={`${reason}-${index}`}
                                      className="bg-neutral-200 text-neutral-700 px-8 py-4 rounded-pill text-xs"
                                    >
                                      {humanize(
                                        reason
                                      )}
                                    </span>
                                  )
                                )}

                              {event
                                .reasons
                                .length >
                                3 && (
                                <span className="text-secondary-light text-xs align-self-center">
                                  +
                                  {event
                                    .reasons
                                    .length -
                                    3}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-secondary-light">
                              —
                            </span>
                          )}
                        </td>

                        <td>
                          <div className="fw-medium">
                            {
                              event.date
                            }
                          </div>

                          {event.time && (
                            <div className="text-secondary-light text-sm">
                              {
                                event.time
                              }
                            </div>
                          )}
                        </td>

                        <td className="text-center">
                          <button
                            type="button"
                            className="w-32-px h-32-px bg-primary-focus text-primary-600 rounded-circle d-inline-flex justify-content-center align-items-center"
                            title="View event"
                            aria-label={`View evaluation ${event.id}`}
                            onClick={() =>
                              navigate(
                                `/events/${encodeURIComponent(
                                  event.id
                                )}`
                              )
                            }
                          >
                            <Icon
                              icon="solar:eye-outline"
                              width="18"
                            />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EventsLayer;